import { supabase } from '@/lib/supabase';
import { getOrSetCache } from '@/lib/coreCache';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 67. OBSERVABILITY
// Prévoir : logs · métriques · erreurs · traces · temps de réponse ·
// utilisation API · utilisation AI Tools.
// Le système doit permettre d’identifier rapidement :
//   erreurs · lenteurs · conflits · appels API excessifs ·
//   problèmes de tenant isolation.
// ─────────────────────────────────────────────────────────────

export const METRIC_CATEGORIES: { key: string; label: string; icon: string }[] = [
  { key: 'Trafic', label: 'Trafic', icon: 'ri-line-chart-line' },
  { key: 'Latence', label: 'Latence', icon: 'ri-timer-line' },
  { key: 'Erreurs', label: 'Erreurs', icon: 'ri-error-warning-line' },
  { key: 'IA', label: 'IA', icon: 'ri-robot-2-line' },
  { key: 'Sécurité', label: 'Sécurité', icon: 'ri-shield-check-line' },
  { key: 'Cache', label: 'Cache', icon: 'ri-database-2-line' },
  { key: 'Système', label: 'Système', icon: 'ri-cpu-line' },
];

export const SEVERITY_META: Record<string, { label: string; className: string; icon: string }> = {
  ok: { label: 'OK', className: 'bg-secondary-100 text-secondary-900', icon: 'ri-checkbox-circle-line' },
  info: { label: 'Info', className: 'bg-primary-100 text-primary-900', icon: 'ri-information-line' },
  warning: { label: 'Attention', className: 'bg-accent-100 text-accent-900', icon: 'ri-alert-line' },
  critical: { label: 'Critique', className: 'bg-red-100 text-red-800', icon: 'ri-error-warning-line' },
};

export function severityMeta(key: string | null | undefined) {
  return SEVERITY_META[key || 'info'] || SEVERITY_META.info;
}

export interface CoreMetric {
  id: number;
  organization_id: number | null;
  metric_key: string;
  label: string | null;
  category: string | null;
  scope: string | null;
  value: number | null;
  unit: string | null;
  severity: string | null;
  recorded_at: string | null;
}

export interface CoreTrace {
  id: number;
  trace_id: string;
  name: string | null;
  kind: string | null;
  status: string | null;
  duration_ms: number | null;
  span_count: number | null;
  error: string | null;
  spans: unknown;
  started_at: string | null;
}

export async function listMetrics(organizationId: number): Promise<CoreMetric[]> {
  const { data, error } = await supabase
    .from('core_observability_metrics')
    .select('*')
    .eq('organization_id', organizationId)
    .order('category', { ascending: true })
    .order('metric_key', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreMetric[];
}

export async function recordMetric(input: {
  tenantId?: number | null;
  organizationId: number;
  metricKey: string;
  label?: string | null;
  category?: string | null;
  scope?: string | null;
  value: number;
  unit?: string | null;
  severity?: string | null;
}): Promise<void> {
  const { error } = await supabase.from('core_observability_metrics').insert({
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    metric_key: input.metricKey,
    label: input.label ?? input.metricKey,
    category: input.category ?? 'Système',
    scope: input.scope ?? null,
    value: input.value,
    unit: input.unit ?? null,
    severity: input.severity ?? 'info',
  });
  if (error) throw error;
}

export async function listTraces(organizationId: number, limit = 50): Promise<CoreTrace[]> {
  const { data, error } = await supabase
    .from('core_traces')
    .select('id, trace_id, name, kind, status, duration_ms, span_count, error, spans, started_at')
    .eq('organization_id', organizationId)
    .order('started_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as CoreTrace[];
}

export interface RecordTraceInput {
  tenantId?: number | null;
  organizationId: number;
  name: string;
  kind?: string;
  status?: 'ok' | 'error' | 'slow';
  durationMs: number;
  spans?: { step: string; duration_ms: number; status?: string }[];
  error?: string | null;
}

export async function recordTrace(input: RecordTraceInput): Promise<void> {
  const traceId = `trace_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
  await supabase.from('core_traces').insert({
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    trace_id: traceId,
    name: input.name,
    kind: input.kind ?? 'request',
    status: input.status ?? 'ok',
    duration_ms: input.durationMs,
    span_count: (input.spans || []).length,
    error: input.error ?? null,
    spans: input.spans ?? [],
    started_at: new Date().toISOString(),
  });
}

export interface ObservabilityStats {
  metrics: number;
  warnings: number;
  criticals: number;
  avgLatency: number;
  traces: number;
  errorTraces: number;
}

export async function fetchObservabilityStats(organizationId: number): Promise<ObservabilityStats> {
  // Agrégat lourd (rapports) servi depuis le cache tenant-aware, invalidé
  // automatiquement par les événements métier (order.*, invoice.*, …).
  return getOrSetCache<ObservabilityStats>({
    organizationId,
    key: 'report:observability:summary',
    scope: 'reports',
    tags: ['reports'],
    ttlSeconds: 120,
    loader: async () => {
      const metrics = await listMetrics(organizationId);
      const traces = await listTraces(organizationId, 100);
      const latencies = metrics.filter((m) => m.category === 'Latence' && typeof m.value === 'number');
      return {
        metrics: metrics.length,
        warnings: metrics.filter((m) => m.severity === 'warning').length,
        criticals: metrics.filter((m) => m.severity === 'critical').length,
        avgLatency: latencies.length ? Math.round(latencies.reduce((a, m) => a + (m.value || 0), 0) / latencies.length) : 0,
        traces: traces.length,
        errorTraces: traces.filter((t) => t.status === 'error').length,
      };
    },
  });
}

/** Diagnostic rapide : liste des signaux à surveiller pour identifier les problèmes. */
export const OBSERVABILITY_SIGNALS: { key: string; label: string; icon: string; hint: string }[] = [
  { key: 'errors', label: 'Erreurs', icon: 'ri-error-warning-line', hint: 'Pics d’erreurs par endpoint et par tenant.' },
  { key: 'slowness', label: 'Lenteurs', icon: 'ri-timer-line', hint: 'Requêtes et outils au-delà du p95.' },
  { key: 'conflicts', label: 'Conflits', icon: 'ri-loop-right-line', hint: 'Conflits de ressource et rejeux idempotents.' },
  { key: 'api_abuse', label: 'Appels API excessifs', icon: 'ri-alarm-warning-line', hint: 'Clients dépassant le rate limit.' },
  { key: 'isolation', label: 'Isolation tenant', icon: 'ri-shield-cross-line', hint: 'Toute fuite inter-tenant est critique.' },
];